/**
 * Vortex Shopify Theme Core Logic
 * Handles AJAX Cart, drawer interactions, and responsive UI components.
 */

document.addEventListener('DOMContentLoaded', () => {
  VortexCart.init();
  VortexUI.init();
});

// ==========================================================================
// VORTEX AJAX CART MANAGER
// ==========================================================================
const VortexCart = {
  init() {
    this.cacheSelectors();
    this.bindEvents();
    this.refreshCartDrawer();
  },

  cacheSelectors() {
    this.drawerOverlay = document.querySelector('.cart-drawer-overlay');
    this.drawer = document.querySelector('.cart-drawer');
    this.drawerClose = document.querySelector('.cart-drawer-close');
    this.drawerTriggers = document.querySelectorAll('.cart-trigger');
    this.cartCountBadges = document.querySelectorAll('.cart-badge');
    this.drawerItemsContainer = document.querySelector('.cart-drawer-items');
    this.drawerSubtotal = document.querySelector('.cart-drawer-subtotal-price');
    this.toast = document.querySelector('.toast-notice');
  },

  bindEvents() {
    // Open/Close Cart Drawer
    if (this.drawerTriggers && this.drawerTriggers.length > 0) {
      this.drawerTriggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.openDrawer();
        });
      });
    }

    if (this.drawerClose) {
      this.drawerClose.addEventListener('click', () => this.closeDrawer());
    }

    if (this.drawerOverlay) {
      this.drawerOverlay.addEventListener('click', () => this.closeDrawer());
    }

    // Capture standard add-to-cart form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.action && form.action.includes('/cart/add')) {
        e.preventDefault();
        this.handleAddToCart(form);
      }
    });

    // Handle interactive quantity buttons inside the Cart Drawer
    if (this.drawerItemsContainer) {
      this.drawerItemsContainer.addEventListener('click', (e) => {
        const target = e.target.closest('.qty-btn');
        if (!target) return;

        const line = target.dataset.line;
        const change = parseInt(target.dataset.change, 10);
        const currentQty = parseInt(target.dataset.qty, 10);
        const newQty = Math.max(0, currentQty + change);

        this.updateCartItem(line, newQty);
      });
    }
  },

  openDrawer() {
    if (this.drawer && this.drawerOverlay) {
      this.drawer.classList.add('active');
      this.drawerOverlay.classList.add('active');
      document.body.style.overflow = 'hidden'; // Lock background scroll
    }
  },

  closeDrawer() {
    if (this.drawer && this.drawerOverlay) {
      this.drawer.classList.remove('active');
      this.drawerOverlay.classList.remove('active');
      document.body.style.overflow = '';
    }
  },

  // Perform AJAX Add to Cart
  async handleAddToCart(form) {
    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn ? submitBtn.innerText : 'Add to Cart';

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerText = 'Adding...';
    }

    const formData = new FormData(form);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Network response was not OK');

      const item = await response.json();
      
      // Refresh Cart drawer data and open it
      await this.refreshCartDrawer();
      
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerText = originalText;
      }

      this.showToast(item.title);
      setTimeout(() => {
        this.openDrawer();
      }, 500);

    } catch (error) {
      console.error('Error adding to cart:', error);
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerText = originalText;
      }
    }
  },

  // Refresh cart items via Shopify GET Cart Ajax Endpoint
  async refreshCartDrawer() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Update Header/Mobile Cart Badges
      this.cartCountBadges.forEach(badge => {
        badge.innerText = cart.item_count;
        badge.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });

      // Populate Items container
      if (!this.drawerItemsContainer) return;

      if (cart.item_count === 0) {
        this.drawerItemsContainer.innerHTML = `
          <div style="text-align: center; margin-top: 4rem;">
            <p style="font-size: 1.1rem; margin-bottom: 2rem;">Your cart is currently empty.</p>
            <a href="/collections/all" class="btn-primary">Shop Our Catalog</a>
          </div>
        `;
        if (this.drawerSubtotal) this.drawerSubtotal.innerText = '$0.00';
        return;
      }

      let itemsHtml = '';
      cart.items.forEach((item, index) => {
        // Format Shopify price (cents to dollars)
        const formattedPrice = this.formatMoney(item.final_line_price);
        
        itemsHtml += `
          <div class="cart-drawer-item">
            <img class="cart-drawer-item-img" src="${item.image || 'https://via.placeholder.com/80'}" alt="${item.product_title}">
            <div class="cart-drawer-item-info">
              <div>
                <h4 class="cart-drawer-item-title">${item.product_title}</h4>
                ${item.variant_title ? `<p style="font-size: 0.75rem; color: rgba(255,255,255,0.4); margin-top: 0.25rem;">${item.variant_title}</p>` : ''}
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 0.5rem;">
                <div class="cart-drawer-item-qty">
                  <button class="qty-btn" data-line="${index + 1}" data-change="-1" data-qty="${item.quantity}">-</button>
                  <span>${item.quantity}</span>
                  <button class="qty-btn" data-line="${index + 1}" data-change="1" data-qty="${item.quantity}">+</button>
                </div>
                <span class="cart-drawer-item-price">${formattedPrice}</span>
              </div>
            </div>
          </div>
        `;
      });

      this.drawerItemsContainer.innerHTML = itemsHtml;
      
      if (this.drawerSubtotal) {
        this.drawerSubtotal.innerText = this.formatMoney(cart.total_price);
      }

    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
    }
  },

  // Update item quantity via AJAX
  async updateCartItem(line, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ line: parseInt(line), quantity: parseInt(quantity) })
      });

      if (!response.ok) throw new Error('Error updating cart item quantity');

      await this.refreshCartDrawer();

    } catch (error) {
      console.error('Error updating cart item:', error);
    }
  },

  // Helper function to format money (cents to USD formatted)
  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  },

  showToast(productTitle) {
    if (!this.toast) return;
    this.toast.querySelector('.toast-product-name').innerText = productTitle;
    this.toast.classList.add('active');
    setTimeout(() => {
      this.toast.classList.remove('active');
    }, 4000);
  }
};

// ==========================================================================
// VORTEX INTERACTIVE UI MANAGER
// ==========================================================================
const VortexUI = {
  init() {
    this.initMobileMenu();
    this.initTabs();
    this.initSearchModal();
  },

  initMobileMenu() {
    const burger = document.querySelector('.mobile-menu-toggle');
    const nav = document.querySelector('.site-nav');

    if (burger && nav) {
      burger.addEventListener('click', () => {
        nav.classList.toggle('active');
        burger.classList.toggle('open');
      });
    }
  },

  initTabs() {
    const tabsContainer = document.querySelector('.tab-headers');
    if (!tabsContainer) return;

    tabsContainer.addEventListener('click', (e) => {
      const btn = e.target.closest('.tab-btn');
      if (!btn) return;

      const tabTarget = btn.dataset.tab;
      
      // Set active tab header
      tabsContainer.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      // Set active pane
      const contentContainer = tabsContainer.nextElementSibling;
      if (contentContainer) {
        contentContainer.querySelectorAll('.tab-pane').forEach(pane => {
          if (pane.id === tabTarget) {
            pane.classList.add('active');
          } else {
            pane.classList.remove('active');
          }
        });
      }
    });
  },

  initSearchModal() {
    const trigger = document.querySelector('.search-trigger');
    const modal = document.querySelector('.search-modal-overlay');
    const closeBtn = document.querySelector('.search-modal-close');

    if (trigger && modal) {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        modal.classList.add('active');
        const input = modal.querySelector('input');
        if (input) setTimeout(() => input.focus(), 200);
      });
    }

    if (closeBtn && modal) {
      closeBtn.addEventListener('click', () => modal.classList.remove('active'));
    }

    // Press ESC to close search modal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal && modal.classList.contains('active')) {
        modal.classList.remove('active');
      }
    });
  }
};
